/**
 * Client-side dwell (stay-point) and co-location detection.
 *
 * Pathfinder's normal dwell pipeline is server-side, computed from the trainee
 * track store — it knows nothing about AdTech pattern-of-life points, which
 * exist only in the browser after a PoL load. This util fills that gap
 * (Tracer-style): it detects dwells directly from loaded PathfinderDataPoints
 * and finds co-locations between those dwells and any other loaded dwells.
 *
 * Output rows use the same shape as the backend dwell rows so they can be
 * merged straight into the `dwellLocations` store (Dwell Analysis tab, map
 * dwell layer, charts, auto-intel all consume that store).
 */

import { PathfinderDataPoint } from '../services/pathfinderApi';

export interface ClientDwell {
  id: string;
  entity_id: string;
  device_id: string;
  device_type: string;
  latitude: number;
  longitude: number;
  duration: string;
  duration_seconds: number;
  start_time: string;
  end_time: string;
  place_name: string;
  place_type: string;
  point_count: number;
  /** Marks rows produced by this util so they can be replaced on reload. */
  client_detected: true;
  source: string;
}

export interface ClientCoLocation {
  entityA: string;
  entityB: string;
  latitude: number;
  longitude: number;
  overlapStart: string;
  overlapEnd: string;
  overlapSeconds: number;
  distanceMeters: number;
}

export interface DwellDetectionOptions {
  /** Max distance (m) from the dwell centroid for a point to extend the stay. */
  radiusMeters?: number;
  /** Minimum stay duration (s) to count as a dwell. */
  minDurationSeconds?: number;
  /** A time gap (s) between consecutive points that breaks a stay. */
  maxGapSeconds?: number;
}

const EARTH_RADIUS_M = 6371000;

export const haversineMeters = (
  lat1: number, lon1: number, lat2: number, lon2: number,
): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
};

const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/**
 * Stay-point detection over loaded data points, grouped per device.
 * Classic algorithm: anchor a candidate cluster and extend it while points
 * stay within `radiusMeters` of the running centroid; emit a dwell when the
 * cluster breaks (by distance or a large time gap) if it lasted long enough.
 */
export function detectDwellsFromPoints(
  points: PathfinderDataPoint[],
  options: DwellDetectionOptions = {},
): ClientDwell[] {
  const radius = options.radiusMeters ?? 75;
  const minDuration = options.minDurationSeconds ?? 10 * 60;
  const maxGap = options.maxGapSeconds ?? 60 * 60;

  // Group by device (studentId + deviceType) and sort by time.
  const byDevice = new Map<string, PathfinderDataPoint[]>();
  for (const p of points || []) {
    if (!p?.coordinates || p.coordinates.length < 2 || !p.timestamp) continue;
    const key = `${p.studentId || 'unknown'}|${p.deviceType || 'phone'}`;
    if (!byDevice.has(key)) byDevice.set(key, []);
    byDevice.get(key)!.push(p);
  }

  const dwells: ClientDwell[] = [];
  byDevice.forEach((devicePoints, key) => {
    const [entityId, deviceType] = key.split('|');
    const sorted = devicePoints
      .slice()
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    let cluster: PathfinderDataPoint[] = [];
    let sumLat = 0;
    let sumLon = 0;

    const flush = () => {
      if (cluster.length >= 2) {
        const start = cluster[0].timestamp;
        const end = cluster[cluster.length - 1].timestamp;
        const seconds = (end.getTime() - start.getTime()) / 1000;
        if (seconds >= minDuration) {
          const lat = sumLat / cluster.length;
          const lon = sumLon / cluster.length;
          dwells.push({
            id: `client-dwell-${entityId}-${start.getTime()}`,
            entity_id: entityId,
            device_id: `${entityId}-${deviceType}`,
            device_type: deviceType,
            latitude: lat,
            longitude: lon,
            duration: formatDuration(seconds),
            duration_seconds: seconds,
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            place_name: `Dwell at ${lat.toFixed(4)}, ${lon.toFixed(4)}`,
            place_type: 'detected',
            point_count: cluster.length,
            client_detected: true,
            source: 'client',
          });
        }
      }
      cluster = [];
      sumLat = 0;
      sumLon = 0;
    };

    for (const p of sorted) {
      // PathfinderDataPoint order: coordinates = [lat, lon].
      const lat = Number(p.coordinates[0]);
      const lon = Number(p.coordinates[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

      if (cluster.length === 0) {
        cluster = [p];
        sumLat = lat;
        sumLon = lon;
        continue;
      }

      const prev = cluster[cluster.length - 1];
      const gap = (p.timestamp.getTime() - prev.timestamp.getTime()) / 1000;
      const cLat = sumLat / cluster.length;
      const cLon = sumLon / cluster.length;
      const dist = haversineMeters(cLat, cLon, lat, lon);

      if (dist <= radius && gap <= maxGap) {
        cluster.push(p);
        sumLat += lat;
        sumLon += lon;
      } else {
        flush();
        cluster = [p];
        sumLat = lat;
        sumLon = lon;
      }
    }
    flush();
  });

  return dwells;
}

/**
 * Find co-locations between two dwell sets: pairs of dwells from different
 * entities whose time intervals overlap by at least `minOverlapSeconds` while
 * their centroids sit within `maxDistanceMeters`. Symmetric duplicates
 * (A↔B / B↔A at the same spot) are collapsed.
 */
export function detectDwellCoLocations(
  dwellsA: Array<Pick<ClientDwell, 'entity_id' | 'latitude' | 'longitude' | 'start_time' | 'end_time'>>,
  dwellsB: Array<Pick<ClientDwell, 'entity_id' | 'latitude' | 'longitude' | 'start_time' | 'end_time'>>,
  opts: { maxDistanceMeters?: number; minOverlapSeconds?: number } = {},
): ClientCoLocation[] {
  const maxDist = opts.maxDistanceMeters ?? 150;
  const minOverlap = opts.minOverlapSeconds ?? 5 * 60;

  const out: ClientCoLocation[] = [];
  const seen = new Set<string>();

  for (const a of dwellsA || []) {
    const aStart = new Date(a.start_time).getTime();
    const aEnd = new Date(a.end_time).getTime();
    if (!Number.isFinite(aStart) || !Number.isFinite(aEnd)) continue;

    for (const b of dwellsB || []) {
      if (b.entity_id === a.entity_id) continue;
      const bStart = new Date(b.start_time).getTime();
      const bEnd = new Date(b.end_time).getTime();
      if (!Number.isFinite(bStart) || !Number.isFinite(bEnd)) continue;

      const start = Math.max(aStart, bStart);
      const end = Math.min(aEnd, bEnd);
      const overlap = (end - start) / 1000;
      if (overlap < minOverlap) continue;

      const dist = haversineMeters(a.latitude, a.longitude, b.latitude, b.longitude);
      if (dist > maxDist) continue;

      const pairKey = [a.entity_id, b.entity_id].sort().join('|') + `|${Math.round(start / 60000)}`;
      if (seen.has(pairKey)) continue;
      seen.add(pairKey);

      out.push({
        entityA: a.entity_id,
        entityB: b.entity_id,
        latitude: (a.latitude + b.latitude) / 2,
        longitude: (a.longitude + b.longitude) / 2,
        overlapStart: new Date(start).toISOString(),
        overlapEnd: new Date(end).toISOString(),
        overlapSeconds: overlap,
        distanceMeters: dist,
      });
    }
  }

  return out.sort((x, y) => y.overlapSeconds - x.overlapSeconds);
}
